/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon, ActionIconDropdown, DetailStack, Dropdown, FontWeight, HStack, ICON_EYE_OUTLINE, Text, UserPreview,
  SimpleTable,
} from '@doveit/bricks';
import { isFuture } from 'date-fns';
import { formatEuro } from '@doveit/hammer';
import {
  Appointment, Prospect, Reminder, SerpProspect,
} from '../../../providers/api/dtos';

import ViewContactPopoverPreview from '../../../contact/containers/view-contact-popover-preview/ViewContactPopoverPreview';
import * as styles from './ViewSerpProspectTableRow.styles';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import UpdateProspectStatus from '../update-prospect-status/UpdateProspectStatus';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { useCurrentAgentIsSameAgentOrManager } from '../../../agent/hooks/use-current-agent-is-same-agent-or-manager/useCurrentAgentIsSameAgentOrManager';
import ViewAgentPopoverPreview from '../../../agent/containers/view-agent-popover-preview/ViewAgentPopoverPreview';
import { ViewAgentSmallPreview } from '../../../agent/components';
import { toAgentPreviewProps } from '../../../agent/mappers/toAgentPreviewProps';
import ViewProspectPopoverPreview from '../view-prospect-popover-preview/ViewProspectPopoverPreview';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import ProspectAvatar from '../../components/prospect-avatar/ProspectAvatar';
import {
  AppointmentStatus, ProspectApprovalStatus, ProspectStatus, ReferenceType,
} from '../../../domain/types';
import { computeResolvableStatus } from '../../../utils/reminder/reminder';
import { ReferenceEntityWithNotes, ResolvableReminderStatus } from '../../../types';
import DateViewer from '../../../components/date-viewer/DateViewer';
import UpdateAppointmentStatus from '../../../reminders/components/update-appointment-status/UpdateAppointmentStatus';
import UpdateProspectApprovalStatus from '../update-prospect-approval-status/UpdateProspectApprovalStatus';
import { goToDetailPage } from '../../../utils/navigate/utils';
import useAssignmentByProspectId from '../../hooks/use-assignment-by-prospect-id/useAssignmentByProspectId';
import useProperty from '../../../property/hooks/use-property/useProperty';
import UpsertReminderAction from '../../../reminders/containers/upsert-reminder-action/UpsertReminderAction';
import SendWhatsappAction from '../../../containers/send-whatsapp-action/SendWhatsappAction';
import { prospectMessagesProvider } from '../../../containers/send-whatsapp-action/messages/prospectMessagesProvider';
import { updateProspect } from '../../../providers/api/prospect/prospectProvider';
import { buildWhatsappMessageSentNote } from '../../../intent/utils/buildWhatsappMessageSentNote';
import UpsertAppointmentAction from '../../../appointment/containers/upsert-appointment-action/UpsertAppointmentAction';
import UpsertNoteAction from '../../../notes/containers/upsert-note-action-v2/UpsertNoteAction';
import PropertyPreview from '../../../property/components/property-preview/PropertyPreview';
import { ThemeIcons } from '../../../theme/icons';
import TextPreview from '../../../components/text-preview/TextPreview';

export interface ViewSerpProspectTableRowProps extends React.AriaAttributes {
  serpProspect: SerpProspect,
  onProspectUpdate?: (prospect: Prospect) => void,
  onAppointmentCreate?: (appointment: Appointment) => void,
  onAppointmentUpdate?: (appointment: Appointment) => void,
  onReminderCreate?: (reminder: Reminder) => void,
}

const ViewSerpProspectTableRow: React.FC<ViewSerpProspectTableRowProps> = ({
  serpProspect,
  onProspectUpdate,
  onAppointmentUpdate,
  onAppointmentCreate,
  onReminderCreate,
  ...rest
}) => {
  const {
    agent, appointment, contact, prospect, reminder, lead,
  } = serpProspect;
  const { data: assignment } = useAssignmentByProspectId(
    prospect.status === ProspectStatus.ASSIGNMENT_CREATED
      ? prospect.id
      : undefined,
  );

  const { data: property } = useProperty(assignment?.propertyId);

  const {
    user, mainUserRole, userIsAdmin,
  } = useRBAC();

  const userIsProspectAgentOrManager = useCurrentAgentIsSameAgentOrManager(agent?.id);

  const reminderToCreate: Reminder = React.useMemo(() => ({
    agentId: agent.id,
    referenceId: prospect.id,
    referenceType: ReferenceType.PROSPECT,
    resolved: false,
  } as Reminder), [agent.id, prospect.id]);

  const appointmentToCreate: Appointment = React.useMemo(() => ({
    agentId: agent.id,
    contactId: prospect.contactId,
    referenceId: prospect.id,
    referenceType: ReferenceType.PROSPECT,
  } as Appointment), [agent?.id, prospect.contactId, prospect.id]);

  const showReminder = React.useMemo(
    () => appointment?.status !== AppointmentStatus.TODO
      && reminder
      && computeResolvableStatus(reminder) === ResolvableReminderStatus.ACTIVE,
    [appointment?.status, reminder],
  );

  const showApprovalStatus = React.useMemo(
    () => (userIsAdmin || userIsProspectAgentOrManager)
      && prospect.agentEvaluation !== undefined
      && prospect.status === ProspectStatus.IN_PROGRESS
      && agent.needsSupervisor, [agent.needsSupervisor, prospect.agentEvaluation, prospect.status, userIsAdmin, userIsProspectAgentOrManager],
  );

  const showAgentEvaluation = React.useMemo(() => prospect.agentEvaluation !== undefined, [prospect.agentEvaluation]);

  const handleWhatsappSend = React.useCallback(async (message: string) => {
    try {
      const updatedProspect = await updateProspect(prospect.id!, {
        ...prospect,
        notes: [{
          text: buildWhatsappMessageSentNote(message),
          author: user.name,
          role: mainUserRole,
        }, ...(prospect.notes || [])],
      });

      onProspectUpdate?.(updatedProspect);
    } catch (_) { /* empty */ }
  }, [mainUserRole, onProspectUpdate, prospect, user.name]);

  const onNoteCreated = React.useCallback((updatedProspect: ReferenceEntityWithNotes) => {
    onProspectUpdate?.(updatedProspect as Prospect);
  }, [onProspectUpdate]);

  const goToProspectPage = React.useCallback(() => {
    goToDetailPage(prospect.id!, ReferenceType.PROSPECT);
  }, [prospect.id]);

  return (
    <SimpleTable.Row {...rest}>
      <SimpleTable.Cell
        aria-label="Contatto"
        width="9rem"
      >
        <ViewContactPopoverPreview
          contact={contact}
          trigger={(
            <div css={styles.userWrapper}>
              <UserPreview
                name={contact.name || NO_VALUE_SYMBOL}
                size="S"
              >
                {contact.phoneNumber}
              </UserPreview>
            </div>
          )}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Immobile"
        width="12rem"
      >
        {!property && (
          <ViewProspectPopoverPreview
            propertyAddress={lead.propertyAddress}
            prospect={prospect}
            trigger={(
              <HStack gap={100} wrap="nowrap">
                <ProspectAvatar
                  size="S"
                  icon={!lead.originAgentId ? ThemeIcons.DOVE : undefined}
                />
                <Text.BodySmall
                  aria-label="Indirizzo immobile"
                  fontWeight={FontWeight.MEDIUM}
                  css={setVerticalEllipsis()}
                >
                  {lead.propertyAddress}
                </Text.BodySmall>
              </HStack>
            )}
          />
        )}
        {property && (
          <PropertyPreview
            property={property}
            showMatchingAction={userIsAdmin || userIsProspectAgentOrManager}
            isPersonal={!!lead.originAgentId}
          />
        )}
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Stato della valutazione"
        width="8.5rem"
      >
        <UpdateProspectStatus
          showAsBadge={!(userIsAdmin || userIsProspectAgentOrManager)}
          prospect={prospect}
          size="XS"
          onSuccess={onProspectUpdate}
        />
      </SimpleTable.Cell>

      <SimpleTable.Cell
        aria-label="Agente"
        width="2rem"
      >
        <ViewAgentPopoverPreview
          agent={agent}
          trigger={(
            <ViewAgentSmallPreview
              {...toAgentPreviewProps(agent, { omit: { name: true } })}
              size="S"
              expanded
            />
          )}
        />
      </SimpleTable.Cell>

      {showReminder && reminder && (
        <SimpleTable.Cell
          aria-label="Promemoria"
          withDivider
          width="26rem"
          colSpan={showAgentEvaluation ? 1 : 2}
        >
          <HStack gap={100} wrap="nowrap">
            <div css={styles.dateWrapper}>
              <DetailStack label="Promemoria">
                <DateViewer
                  stringDate={reminder.startDate}
                  checkIfToday
                  checkIfTomorrow
                />
              </DetailStack>
            </div>
            {reminder.notes && (
              <TextPreview text={reminder.notes} />
            )}
          </HStack>
        </SimpleTable.Cell>
      )}

      {
        !showReminder && (
          <SimpleTable.Cell
            aria-label="Appuntamento"
            withDivider
            colSpan={showAgentEvaluation ? 1 : 2}
          >
            <HStack gap={100}>
              <div css={styles.dateWrapper}>
                <DetailStack
                  label="Appuntamento"
                >
                  {appointment ? (
                    <DateViewer
                      stringDate={appointment.startDate}
                      checkIfToday
                      checkIfTomorrow
                      withHour={isFuture(appointment.startDate)}
                    />
                  ) : 'Nessuno'}
                </DetailStack>
              </div>
              {appointment && (
                <UpdateAppointmentStatus
                  appointment={appointment}
                  onSuccess={onAppointmentUpdate}
                  showAsBadge={!(userIsAdmin || userIsProspectAgentOrManager)}
                />
              )}
            </HStack>
          </SimpleTable.Cell>
        )
      }

      {showAgentEvaluation && (
        <SimpleTable.Cell>
          <HStack
            aria-label="Valutazione"
            gap={100}
            wrap="nowrap"
          >
            <DetailStack
              label="Valutazione"
              color={prospect.approval && prospect.approval.status !== ProspectApprovalStatus.APPROVED
                ? 'critical.default.high'
                : 'neutral.default.high'}
            >
              {formatEuro(prospect.agentEvaluation!)}
            </DetailStack>
            {showApprovalStatus && (
              <UpdateProspectApprovalStatus
                prospect={prospect}
                onSuccess={onProspectUpdate}
                size="XS"
              />
            )}
          </HStack>
        </SimpleTable.Cell>
      )}

      <SimpleTable.Cell
        aria-label="Azioni"
        align="right"
      >
        <HStack
          gap={75}
          wrap="nowrap"
        >
          <ActionIcon
            aria-label="Vai alla pagina della valutazione"
            label="Visualizza"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToProspectPage}
            size="S"
          />
          {(userIsAdmin || userIsProspectAgentOrManager) && (
            <ActionIconDropdown
              aria-label="Visualizza azioni secondarie"
              label="Azioni"
              size="S"
              emphasis="low"
            >
              {contact.phoneNumber && (
                <SendWhatsappAction
                  phoneNumber={contact.phoneNumber}
                  name={contact.name}
                  fetchMessages={prospectMessagesProvider(prospect, agent)}
                  onMessageSent={handleWhatsappSend}
                >
                  {({ onClick }) => (
                    <Dropdown.Option
                      label="Invia whatsapp"
                      onClick={onClick}
                    />
                  )}
                </SendWhatsappAction>
              )}
              <UpsertReminderAction
                reminder={reminderToCreate}
                onSuccess={onReminderCreate}
              >
                {({ upsert }) => (
                  <Dropdown.Option
                    label="Aggiungi promemoria"
                    onClick={upsert}
                  />
                )}
              </UpsertReminderAction>
              <UpsertAppointmentAction
                appointment={appointmentToCreate}
                onSuccess={onAppointmentCreate}
              >
                {({ upsert }) => (
                  <Dropdown.Option
                    label="Aggiungi appuntamento"
                    onClick={upsert}
                  />
                )}
              </UpsertAppointmentAction>
              <UpsertNoteAction
                reference={prospect}
                referenceType={ReferenceType.PROSPECT}
                onSuccess={onNoteCreated}
              >
                {({ upsert }) => (
                  <Dropdown.Option
                    label="Aggiungi nota"
                    onClick={upsert}
                  />
                )}
              </UpsertNoteAction>
            </ActionIconDropdown>
          )}
        </HStack>
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default ViewSerpProspectTableRow;
